import { FormEvent, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import fetchProfile from "../../../api/userProfile";
import useFileVersionList from "./hooks/useFileVersionList";
import "./css/useFileVersionList.scss";

interface UpdateFile {
  name: string;
  path: string;
  test: boolean;
}

const FileVersionList = () => {
  const hook = useFileVersionList();
  const navigate = useNavigate();
  const { modalIsOpen, setModalIsOpen } = hook;
  const files = hook.data as UpdateFile[];
  const serviceNames = Object.keys(hook.serviceList);
  const mainCount = useMemo(() => files.filter((item) => !item.test).length, [files]);
  const testCount = files.length - mainCount;

  useEffect(() => {
    const verifyPermission = async () => {
      const profileData = await fetchProfile();
      if (profileData.data.userGroup !== "admin" && profileData.data.userGroup !== "ai") {
        navigate("/");
      }
    };

    verifyPermission();
  }, [navigate]);

  useEffect(() => {
    if (!modalIsOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setModalIsOpen(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [modalIsOpen, setModalIsOpen]);

  const submitService = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (hook.input.trim()) hook.saveService();
  };

  return (
    <section id="update-file-manager">
      <header className="update-file__header">
        <div>
          <span>UPDATE DISTRIBUTION</span>
          <h1>업데이트 파일 관리</h1>
          <p>서비스별 앱과 펌웨어 파일의 배포 상태를 관리합니다.</p>
        </div>
        <button type="button" onClick={() => hook.setModalIsOpen(true)}>
          서비스 추가 <span aria-hidden="true">＋</span>
        </button>
      </header>

      <div className="update-file__toolbar" aria-label="업데이트 파일 조회 조건">
        <label>
          <span>서비스</span>
          <select value={hook.service} onChange={(event) => hook.setService(event.target.value)}>
            {serviceNames.map((service) => (
              <option value={service} key={service}>{service}</option>
            ))}
          </select>
        </label>
        <label>
          <span>파일 유형</span>
          <select value={hook.type} onChange={(event) => hook.setType(event.target.value)}>
            {hook.typeList.map((type) => (
              <option value={type} key={type}>{type}</option>
            ))}
          </select>
        </label>
        <div className="update-file__selection">
          <span>CURRENT SELECTION</span>
          <strong>{hook.service || "서비스 선택"}</strong>
          <small>{hook.type || "파일 유형"}</small>
        </div>
      </div>

      <div className="update-file__summary">
        <article>
          <span>전체 파일</span>
          <strong>{files.length.toLocaleString()}<small>개</small></strong>
        </article>
        <article className="is-main">
          <span>MAIN 배포</span>
          <strong>{mainCount.toLocaleString()}<small>개</small></strong>
        </article>
        <article className="is-test">
          <span>TEST 대기</span>
          <strong>{testCount.toLocaleString()}<small>개</small></strong>
        </article>
      </div>

      <section className="update-file__panel" aria-label="업데이트 파일 목록">
        <header>
          <div>
            <strong>파일 목록</strong>
            <span>TEST 파일을 배포하면 MAIN 상태로 전환됩니다.</span>
          </div>
          <span>{hook.service} · {hook.type}</span>
        </header>

        <div className="update-file__table-wrap">
          <table>
            <thead>
              <tr>
                <th>파일명</th>
                <th>상태</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {files.map((item) => (
                <tr key={item.path || item.name}>
                  <td>
                    <span className="update-file__file-icon" aria-hidden="true">↓</span>
                    <div>
                      <strong title={item.name}>{item.name}</strong>
                      <small title={item.path}>{item.path}</small>
                    </div>
                  </td>
                  <td>
                    <span className={`update-file__status ${item.test ? "is-test" : "is-main"}`}>
                      <i />{item.test ? "TEST" : "MAIN"}
                    </span>
                  </td>
                  <td>
                    <div className="update-file__actions">
                      <button type="button" onClick={() => hook.download(item.path)}>다운로드</button>
                      <button
                        type="button"
                        className={item.test ? "is-deploy" : "is-withdraw"}
                        onClick={() => hook.changeStatus(item.path, item.test)}
                      >
                        {item.test ? "배포" : "철회"}
                      </button>
                      {item.test ? (
                        <button type="button" className="is-danger" onClick={() => hook.deleteFile(item.path)}>삭제</button>
                      ) : (
                        <button type="button" onClick={() => hook.updateHistory(item.path)}>기록</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {files.length === 0 && (
                <tr>
                  <td colSpan={3} className="update-file__empty">선택한 조건에 등록된 파일이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {hook.modalIsOpen && (
        <div className="update-service-modal" role="presentation" onMouseDown={() => hook.setModalIsOpen(false)}>
          <section
            className="update-service-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-service-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span>SERVICE DIRECTORY</span>
                <h2 id="update-service-modal-title">서비스 추가</h2>
                <p>업데이트 파일을 구분할 새로운 서비스 폴더를 생성합니다.</p>
              </div>
              <button type="button" aria-label="서비스 추가 닫기" onClick={() => hook.setModalIsOpen(false)}>×</button>
            </header>
            <form onSubmit={submitService}>
              <label htmlFor="update-service-name">서비스 이름</label>
              <div>
                <input
                  id="update-service-name"
                  type="text"
                  value={hook.input}
                  onChange={(event) => hook.setInput(event.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
                  placeholder="예: ADDDILIGHTSBA"
                  autoComplete="off"
                  autoFocus
                />
                <button type="submit" disabled={!hook.input.trim()}>추가</button>
              </div>
            </form>
            <div className="update-service-modal__services">
              <span>등록된 서비스</span>
              <div>
                {serviceNames.map((service) => <small key={service}>{service}</small>)}
              </div>
            </div>
          </section>
        </div>
      )}
    </section>
  );
};

export default FileVersionList;
